import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  approveItem,
  bulkApprove,
  fetchApprovals,
  rejectItem,
} from '../api';

export function useApprovals(activeTab = 'all') {
  return useGrowthQuery({
    queryKey: growthKeys.approvals.list({ uiBucket: activeTab }),
    queryFn: () => fetchApprovals(activeTab === 'all' ? undefined : activeTab),
  });
}

export function useApprovalMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'approvals'] });

  const approve = useMutation({
    mutationFn: approveItem,
    onSuccess: () => {
      notify.success('Approved');
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: growthKeys.dashboard() });
    },
    onError: (e) => notify.error(e),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectItem(id, reason),
    onSuccess: () => {
      notify.success('Rejected');
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: growthKeys.dashboard() });
    },
    onError: (e) => notify.error(e),
  });

  const bulk = useMutation({
    mutationFn: bulkApprove,
    onSuccess: () => {
      notify.success('Bulk approved');
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: growthKeys.dashboard() });
    },
    onError: (e) => notify.error(e),
  });

  return { approve, reject, bulk };
}
